import { alpha, styled } from '@mui/material/styles';
import { Button } from 'components/ui/Button';
import { getThemeSpacingCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles } from 'theme';

interface ActiveStyleProps {
  isActive?: boolean;
}

interface ExpandedStyleProps {
  isExpanded?: boolean;
}

interface PendingStyleProps {
  isPending?: boolean;
}

interface ShowMoreButtonStyleProps {
  placement?: 'bottom' | 'top';
}

interface ZoomStyleProps {
  zoomLevel: number;
}

const shouldForwardActiveProp = (prop: PropertyKey) => prop !== 'isActive';
const shouldForwardExpandedProp = (prop: PropertyKey) => prop !== 'isExpanded';
const shouldForwardHeaderProp = (prop: PropertyKey) => (
  shouldForwardActiveProp(prop) && prop !== 'isPending'
);
const shouldForwardShowMoreButtonProp = (prop: PropertyKey) => prop !== 'placement';
const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';

export const MeasurementDatesAccordionPanelRoot = styled('section', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  '--checkbox-selection-border-width': getCssPx(1),
  '--checkbox-selection-check-height': getCssPx(5),
  '--checkbox-selection-check-width': getCssPx(9),
  '--checkbox-selection-indeterminate-width': getCssPx(8),
  '--checkbox-selection-mark-stroke-width': getCssPx(2),
  '--checkbox-selection-radius': getCssPx(theme.shape.radius.xs),
  '--checkbox-selection-size': getCssPx(16),
  '--icon-button-compact-icon-size': getCssPx(16),
  '--icon-button-compact-size': getCssPx(20),
  '--icon-button-radius': getCssPx(theme.shape.radius.xs),
  '--measurement-dates-accordion-border-width': getCssPx(1),
  '--measurement-dates-accordion-checkbox-size': 'var(--checkbox-selection-size)',
  '--measurement-dates-accordion-control-height': getCssPx(16),
  '--measurement-dates-accordion-control-width': getCssPx(24),
  '--measurement-dates-accordion-font-size-l': getCssPx(14),
  '--measurement-dates-accordion-font-size-m': getCssPx(12),
  '--measurement-dates-accordion-font-size-s': getCssPx(10),
  '--measurement-dates-accordion-gap-xs': getThemeSpacingCssPx(theme, 'xs'),
  '--measurement-dates-accordion-line-height-l': getCssPx(20),
  '--measurement-dates-accordion-line-height-m': getCssPx(20),
  '--measurement-dates-accordion-line-height-s': getCssPx(16),
  '--measurement-dates-accordion-radius-s': getCssPx(theme.shape.radius.s),
  '--measurement-dates-accordion-radius-xs': getCssPx(theme.shape.radius.xs),
  '--measurement-dates-accordion-row-height': getCssPx(24),
  '--prism-scrollbar-thumb-border-color': theme.palette.neutral[100],
  '--prism-scrollbar-thumb-border-width': getCssPx(2),
  '--prism-scrollbar-width': getCssPx(8),
  '--measurement-dates-accordion-spacing-xs': getThemeSpacingCssPx(theme, 'xs'),
  '--measurement-dates-accordion-spacing-s': getThemeSpacingCssPx(theme, 's'),
  '--measurement-dates-accordion-spacing-m': getThemeSpacingCssPx(theme, 'm'),
  '--measurement-dates-accordion-state-padding-x': getThemeSpacingCssPx(theme, 'm'),
  '--measurement-dates-accordion-state-padding-y': getThemeSpacingCssPx(theme, 'xl'),
  '--scroll-hidden-items-hint-border-width': 'var(--measurement-dates-accordion-border-width)',
  '--scroll-hidden-items-hint-font-size': 'var(--measurement-dates-accordion-font-size-m)',
  '--scroll-hidden-items-hint-gap': 'var(--measurement-dates-accordion-gap-xs)',
  '--scroll-hidden-items-hint-icon-size': 'var(--measurement-dates-accordion-font-size-m)',
  '--scroll-hidden-items-hint-line-height': 'var(--measurement-dates-accordion-line-height-m)',
  '--scroll-hidden-items-hint-offset': 'var(--measurement-dates-accordion-spacing-xs)',
  '--scroll-hidden-items-hint-padding': 'var(--measurement-dates-accordion-spacing-xs) var(--measurement-dates-accordion-spacing-s)',
  '--scroll-hidden-items-hint-radius': 'var(--measurement-dates-accordion-radius-xs)',
  backgroundColor: theme.palette.neutral[100],
  boxSizing: 'border-box',
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  padding: 'var(--measurement-dates-accordion-spacing-s)',
  width: '100%',
}));

export const MeasurementDatesAccordionPanelTitle = styled('h2')(({ theme }) => ({
  color: theme.palette.text.primary,
  flexShrink: 0,
  fontSize: 'var(--measurement-dates-accordion-font-size-l)',
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: 0,
  lineHeight: 'var(--measurement-dates-accordion-line-height-l)',
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const MeasurementDatesAccordionPanelList = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  minHeight: 0,
  overflowY: 'auto',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: 'var(--prism-scrollbar-thumb-border-color)',
    thumbBorderWidth: 'var(--prism-scrollbar-thumb-border-width)',
    width: 'var(--prism-scrollbar-width)',
  }),
}));

export const MeasurementDatesAccordionPanelListFrame = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  minWidth: 0,
  position: 'relative',
});

export const MeasurementDatesAccordionPanelSection = styled('article', {
  shouldForwardProp: shouldForwardActiveProp,
})<ActiveStyleProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `var(--measurement-dates-accordion-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  padding: 'var(--measurement-dates-accordion-spacing-xs)',
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const MeasurementDatesAccordionPanelHeader = styled('div', {
  shouldForwardProp: shouldForwardHeaderProp,
})<ActiveStyleProps & PendingStyleProps>(({ isActive = false, isPending = false, theme }) => ({
  '--measurement-dates-accordion-content-color': isPending ? theme.palette.brand[800] : theme.palette.neutral[500],
  '--measurement-dates-accordion-metadata-color': isPending ? theme.palette.brand[700] : theme.palette.neutral[400],
  alignItems: 'center',
  backgroundColor: isPending ? theme.palette.brand[100] : theme.palette.neutral[200],
  boxSizing: 'border-box',
  border: `var(--measurement-dates-accordion-border-width) solid ${
    isPending ? theme.palette.brand[400] : 'transparent'
  }`,
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  color: isPending ? theme.palette.brand[800] : theme.palette.neutral[500],
  cursor: isActive || isPending ? 'default' : 'pointer',
  display: 'grid',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  gridTemplateColumns: 'auto auto minmax(0, auto) minmax(0, 1fr) auto auto auto',
  minHeight: 'var(--measurement-dates-accordion-row-height)',
  minWidth: 0,
  outline: 0,
  padding: '0 var(--measurement-dates-accordion-spacing-xs)',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: '100%',
  ...(!isActive && !isPending
    ? {
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.neutral[100],
        borderColor: theme.palette.neutral[200],
        color: theme.palette.neutral[700],
      },
    }
    : {}),
  '&:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    borderColor: theme.palette.brand[400],
    color: theme.palette.neutral[700],
  },
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-dates-accordion-control-height)',
  },
  '& .MuiCircularProgress-root': {
    color: theme.palette.brand[500],
  },
  '&:hover [data-row-action], &:focus-within [data-row-action]': {
    opacity: 1,
  },
  '@container (max-width: 760px)': {
    '& [data-measurement-date-inline-action-label="true"]': {
      display: 'none',
    },
  },
  '@container (max-width: 640px)': {
    gridTemplateColumns: 'auto auto minmax(0, 1fr) auto auto auto',
    '& [data-measurement-date-metadata="true"]': {
      display: 'none',
    },
  },
}));

export const MeasurementDatesAccordionPanelIcon = styled('span')({
  alignItems: 'center',
  color: 'var(--measurement-dates-accordion-content-color)',
  display: 'inline-flex',
  height: 'var(--measurement-dates-accordion-control-height)',
  justifyContent: 'center',
  width: 'var(--measurement-dates-accordion-control-width)',
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-dates-accordion-control-height)',
  },
});

export const MeasurementDatesAccordionPanelChevron = styled(MeasurementDatesAccordionPanelIcon, {
  shouldForwardProp: shouldForwardExpandedProp,
})<ExpandedStyleProps>(({ isExpanded = false, theme }) => ({
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const MeasurementDatesAccordionPanelDate = styled('span')(({ theme }) => ({
  color: 'var(--measurement-dates-accordion-content-color)',
  fontSize: 'var(--measurement-dates-accordion-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-dates-accordion-line-height-m)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const MeasurementDatesAccordionPanelMetadata = styled('span')(({ theme }) => ({
  color: 'var(--measurement-dates-accordion-metadata-color)',
  fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const MeasurementDatesAccordionPanelInlineAction = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  color: theme.palette.brand[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  fontWeight: theme.typography.fontWeightMedium,
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
  minHeight: 'var(--measurement-dates-accordion-row-height)',
  padding: '0 var(--measurement-dates-accordion-spacing-xs)',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-dates-accordion-control-height)',
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    color: theme.palette.brand[700],
    outline: 0,
  },
  '&:disabled': {
    color: alpha(theme.palette.brand[500], 0.45),
    cursor: 'default',
  },
}));

export const MeasurementDatesAccordionPanelBody = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  display: 'grid',
  fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
  padding: 0,
}));

export const MeasurementDatesAccordionPanelShowMoreButton = styled(Button, {
  shouldForwardProp: shouldForwardShowMoreButtonProp,
})<ShowMoreButtonStyleProps>(({ placement = 'bottom', theme }) => ({
  alignSelf: 'center',
  marginBottom: placement === 'top' ? 'var(--measurement-dates-accordion-spacing-xs)' : 0,
  marginTop: placement === 'bottom' ? 'var(--measurement-dates-accordion-spacing-xs)' : 0,
  minHeight: 'var(--measurement-dates-accordion-row-height)',
  padding: 'var(--measurement-dates-accordion-spacing-xs) var(--measurement-dates-accordion-spacing-s)',
  textTransform: 'none',
  '&, &.MuiButton-text': {
    borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
    color: theme.palette.brand[500],
    fontSize: 'var(--measurement-dates-accordion-font-size-s)',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
    minHeight: 'var(--measurement-dates-accordion-row-height)',
    padding: 'var(--measurement-dates-accordion-spacing-xs) var(--measurement-dates-accordion-spacing-s)',
    textTransform: 'none',
  },
  '&.Mui-disabled, &.MuiButton-text.Mui-disabled': {
    backgroundColor: 'transparent',
    color: alpha(theme.palette.brand[500], 0.5),
    cursor: 'progress',
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    marginLeft: 0,
    marginRight: 0,
  },
  '& .MuiButton-startIcon > *:first-of-type, & .MuiButton-endIcon > *:first-of-type, & svg': {
    fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  },
}));

export const MeasurementDatesAccordionPanelSkeletonList = styled('div')({
  display: 'grid',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  minWidth: 0,
});

export const MeasurementDatesAccordionPanelSkeletonSection = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `var(--measurement-dates-accordion-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  padding: 'var(--measurement-dates-accordion-spacing-xs)',
}));

export const MeasurementDatesAccordionPanelSkeletonRow = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[200],
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  gridTemplateColumns: 'auto auto minmax(0, 1fr) auto',
  minHeight: 'var(--measurement-dates-accordion-row-height)',
  padding: '0 var(--measurement-dates-accordion-spacing-xs)',
}));

export const MeasurementDatesAccordionPanelState = styled('div')(({ theme }) => ({
  alignItems: 'baseline',
  backgroundColor: theme.palette.background.paper,
  border: `var(--measurement-dates-accordion-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--measurement-dates-accordion-radius-xs)',
  boxSizing: 'border-box',
  color: theme.palette.text.secondary,
  display: 'flex',
  flex: '0 0 auto',
  flexWrap: 'wrap',
  gap: 'var(--measurement-dates-accordion-gap-xs)',
  justifyContent: 'center',
  marginTop: 'var(--measurement-dates-accordion-spacing-xs)',
  padding: 'var(--measurement-dates-accordion-spacing-s) var(--measurement-dates-accordion-state-padding-x)',
  textAlign: 'center',
}));

export const MeasurementDatesAccordionPanelStateTitle = styled('strong')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
}));

export const MeasurementDatesAccordionPanelStateDescription = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 'var(--measurement-dates-accordion-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-dates-accordion-line-height-s)',
}));
